/** Curated per-concern entity maps. Every id verified against the live state machine. */

export const energy = {
  livePower: 'sensor.tibber_pulse_dirigentboligen_power',
  avgPower: 'sensor.tibber_pulse_dirigentboligen_average_power',
  maxPower: 'sensor.tibber_pulse_dirigentboligen_max_power',
  minPower: 'sensor.tibber_pulse_dirigentboligen_min_power',
  accumulatedToday: 'sensor.tibber_pulse_dirigentboligen_accumulated_consumption',
  accumulatedCost: 'sensor.tibber_pulse_dirigentboligen_accumulated_cost',
  estimatedHour: 'sensor.tibber_pulse_dirigentboligen_estimated_consumption_current_hour',
  powerFactor: 'sensor.tibber_pulse_dirigentboligen_power_factor',
  signal: 'sensor.tibber_pulse_dirigentboligen_signal_strength',
  phases: {
    v1: 'sensor.tibber_pulse_dirigentboligen_voltage_phase1',
    v2: 'sensor.tibber_pulse_dirigentboligen_voltage_phase2',
    v3: 'sensor.tibber_pulse_dirigentboligen_voltage_phase3',
    l1: 'sensor.tibber_pulse_dirigentboligen_current_l1',
    l3: 'sensor.tibber_pulse_dirigentboligen_current_l3',
  },
  price: 'sensor.nordpool',
  priceTimeline: 'sensor.nordpool_prices_timeline',
  priceActual: 'sensor.norgespris_actual_price',
  priceTotalWithNettleie: 'sensor.norgespris_total_price_with_nettleie',
  nettleieNow: 'sensor.norgespris_current_nettleie',
  dailyCost: 'sensor.norgespris_daily_cost',
  monthlyCost: 'sensor.norgespris_monthly_cost',
  dailyKwh: 'sensor.norgespris_daglig_kwh',
  monthlyKwh: 'sensor.norgespris_manedlig_kwh',
  monthlyCapStatus: 'sensor.norgespris_monthly_cap_status',
  monthlyCap: 'input_number.norgespris_monthly_cap',
  peakStatus: 'sensor.norgespris_peak_power_status',
  peakThreshold: 'input_number.norgespris_peak_power_threshold',
  peakWarnPercent: 'input_number.norgespris_peak_warning_percent',
  peakMonitoring: 'input_boolean.norgespris_peak_monitoring',
  budgetDaily: 'input_number.norgespris_daily_budget',
  budgetRemaining: 'sensor.norgespris_budget_remaining',
  monthlyBudgetRemaining: 'sensor.norgespris_monthly_budget_remaining',
} as const

export const chargers = {
  zaptec: {
    power: 'sensor.zaptec_total_charging_power',
    status: 'sensor.zaptec_charging_status',
    sessionEnergy: 'sensor.zaptec_session_energy',
    circuitUtil: 'sensor.zaptec_circuit_utilization',
    activeCharging: 'binary_sensor.zaptec_active_charging',
    ready: 'binary_sensor.zaptec_ready_to_charge',
    enabled: 'input_boolean.zaptec_charging_enabled',
    autoStart: 'input_boolean.zaptec_auto_start',
    maxAmps: 'input_number.zaptec_max_charging_amps',
    defaultAmps: 'input_number.zaptec_default_charging_amps',
    start: 'script.zaptec_start_charging',
    stop: 'script.zaptec_stop_charging',
    chargeMax: 'script.zaptec_charge_at_max',
  },
  amina: {
    power: 'sensor.amina_s_ev_charger_power',
    totalPower: 'sensor.amina_s_ev_charger_total_active_power',
    lastSession: 'sensor.amina_s_ev_charger_last_session_energy',
    totalEnergy: 'sensor.amina_s_ev_charger_total_active_energy',
    evStatus: 'sensor.amina_s_ev_charger_ev_status',
    connected: 'binary_sensor.amina_s_ev_charger_ev_connected',
    charging: 'binary_sensor.amina_s_ev_charger_charging',
    limit: 'number.amina_s_ev_charger_charge_limit',
    enable: 'switch.amina_s_ev_charger',
  },
} as const

export const car = {
  battery: 'sensor.tessie_battery',
  range: 'sensor.tessie_range',
  chargerPower: 'sensor.tessie_charger_power',
  chargingRate: 'sensor.tessie_charging_rate',
  energyAdded: 'sensor.tessie_energy_added',
  odometer: 'sensor.tessie_odometer',
  shiftState: 'sensor.tessie_shift_state',
  tempInside: 'sensor.tessie_temperature_inside',
  tempOutside: 'sensor.tessie_temperature_outside',
  tpms: [
    'sensor.tessie_tpms_front_left',
    'sensor.tessie_tpms_front_right',
    'sensor.tessie_tpms_rear_left',
    'sensor.tessie_tpms_rear_right',
  ],
  charging: 'binary_sensor.tessie_charging',
  plugged: 'binary_sensor.tessie_charger',
  online: 'binary_sensor.tessie_online',
  asleep: 'binary_sensor.tessie_asleep',
  userPresent: 'binary_sensor.tessie_user_present',
  chargeLimit: 'number.tessie_charge_limit',
  chargingAmps: 'number.tessie_charging_amps',
  chargerSwitch: 'switch.tessie_charger',
  sentry: 'switch.tessie_sentry_mode',
  climate: 'climate.tessie_hvac_climate_system',
  doors: 'lock.tessie_doors',
  chargePortLatch: 'lock.tessie_charge_port_latch',
  chargePortDoor: 'cover.tessie_charger_door',
  frunk: 'cover.tessie_frunk',
  trunk: 'cover.tessie_trunk',
  location: 'device_tracker.tessie_location_tracker',
} as const

export const climateExtras = {
  heatpump: 'climate.varmepumpe',
} as const

export const security = {
  lock: 'lock.inngangsdor',
  alarm: 'alarm_control_panel.dream_machine_pro_alarmbehandling',
  camera: 'camera.uvc_g3_high_resolution_channel',
} as const

export const people = ['person.erlend', 'person.mina', 'person.eirik', 'person.ingeborg'] as const

export const weather = {
  primary: 'weather.taugata_24',
  forecast: 'weather.forecast_taugata_24',
  station: 'weather.iron_nerd_weather_station_2',
  stationHourly: 'weather.iron_nerd_weather_station_hourly',
} as const

export const household = {
  todos: ['todo.inbox_2', 'todo.home_tech_2', 'todo.arbeidet_mitt_2', 'todo.eirik_tasks_2'],
  calendars: [
    'calendar.inbox_2',
    'calendar.home_tech_2',
    'calendar.arbeidet_mitt_2',
    'calendar.eirik_tasks_2',
    'calendar.heating',
  ],
  waste: 'sensor.restavfall',
  vacuum: 'vacuum.roborock_de_260443705_s5',
} as const

/** Per-device energy breakdown for the Energy screen (verified entity ids). */
export const perDeviceEnergy = {
  waterHeater: {
    name: 'Varmtvannsbereder',
    power: 'sensor.heavy_duty_switch_electric_consumption_w',
    energy: 'sensor.heavy_duty_switch_electric_consumption_kwh',
    lifetime: 'sensor.vvb_kwh_load',
    temp: 'sensor.heavy_duty_switch_air_temperature',
    control: 'switch.heavy_duty_switch',
  },
  dryer: { name: 'Tørketrommel', energy: 'sensor.bad_torketrommel_energy', control: 'switch.torketrommel_shelly' },
  radiators: [
    { name: 'Kjellerstua', power: 'sensor.kjellerstua_current_power', daily: 'sensor.kjellerstua_daglig_forbruk', yearly: 'sensor.kjellerstua_arlig_forbruk' },
    { name: 'Kontor kjeller', power: 'sensor.kjeller_kontor_current_power', daily: 'sensor.kjeller_kontor_daglig_forbruk', yearly: 'sensor.kjeller_kontor_arlig_forbruk' },
    { name: 'Hovedsoverom', power: 'sensor.masterbedroom_ovn_current_power', daily: 'sensor.masterbedroom_ovn_daglig_forbruk', yearly: 'sensor.masterbedroom_ovn_arlig_forbruk' },
    { name: 'Gulvvarme bad kjeller', power: 'sensor.bad_kjeller_floor_heating_power' },
  ],
  houseLivePower: 'sensor.current_power',
  housePowerNative: 'sensor.power_monitor_native',
  monthlyNet: 'sensor.dirigentboligen_monthly_net_consumption',
  monthlyPeakHour: 'sensor.dirigentboligen_monthly_peak_hour_consumption',
} as const

/** Custom template alerts — the house's warning layer. Surfaced as a Varsler center. */
export const alerts: Array<{ id: string; label: string; severity: 'warn' | 'danger' }> = [
  { id: 'binary_sensor.effekttopp_varsel_nettleie', label: 'Effekttopp (nettleie)', severity: 'warn' },
  { id: 'binary_sensor.energy_power_spike_alert', label: 'Effekt-spike', severity: 'warn' },
  { id: 'binary_sensor.energy_cost_threshold_alert', label: 'Strømkostnad over terskel', severity: 'warn' },
  { id: 'binary_sensor.over_daglig_strombudsjett', label: 'Over daglig strømbudsjett', severity: 'warn' },
  { id: 'binary_sensor.energy_system_health_critical', label: 'Energisystem kritisk', severity: 'danger' },
  { id: 'binary_sensor.mcp_flood_alert', label: 'Vannlekkasje', severity: 'danger' },
  { id: 'binary_sensor.varmepumpe_circuit_overload_warning', label: 'Varmepumpe overbelastning', severity: 'danger' },
  { id: 'binary_sensor.heat_warning', label: 'Varmevarsel', severity: 'warn' },
  { id: 'binary_sensor.uv_warning', label: 'UV-varsel', severity: 'warn' },
  { id: 'binary_sensor.lightning_warning', label: 'Lyn i nærheten', severity: 'warn' },
  { id: 'binary_sensor.poor_air_quality_alert', label: 'Dårlig luftkvalitet', severity: 'warn' },
  { id: 'binary_sensor.battery_low_devices', label: 'Lavt batteri på enheter', severity: 'warn' },
  { id: 'binary_sensor.backups_stale', label: 'Backup utdatert', severity: 'warn' },
  { id: 'binary_sensor.memory_leak_detected', label: 'Minnelekkasje (HA)', severity: 'warn' },
  { id: 'binary_sensor.samsung_tv_system_health', label: 'Samsung TV helse', severity: 'warn' },
  { id: 'binary_sensor.indoor_plant_needs_water', label: 'Plante trenger vann', severity: 'warn' },
  { id: 'binary_sensor.weather_suitable_for_garden_watering', label: 'Fint for hagevanning', severity: 'warn' },
]

/** Presence depth beyond the person entities. */
export const presence = {
  houseOccupied: 'binary_sensor.house_occupied',
  peopleHome: 'binary_sensor.people_home',
  nerdsHome: 'binary_sensor.nerds_home',
  parentsHome: 'binary_sensor.parents_home',
  peopleSleeping: 'binary_sensor.people_sleeping',
  erlendInBed: 'binary_sensor.erlend_is_in_bed',
  teslaAtHome: 'binary_sensor.tesla_at_home',
  perPerson: {
    erlend: 'binary_sensor.erlend_home',
    mina: 'binary_sensor.mina_home',
    eirik: 'binary_sensor.eirik_home',
    ingeborg: 'binary_sensor.ingeborg_home',
  },
} as const

export const sunWeather = {
  nextRising: 'sensor.sun_next_rising_2',
  nextSetting: 'sensor.sun_next_setting_2',
  nextDawn: 'sensor.sun_next_dawn_2',
  nextDusk: 'sensor.sun_next_dusk_2',
  uvIndex: 'sensor.openweathermap_uv_index',
  feelsLike: 'sensor.openweathermap_feels_like_temperature',
  pressure: 'sensor.openweathermap_pressure',
  outdoorHumidity: 'sensor.openweathermap_humidity',
  dewPoint: 'sensor.openweathermap_dew_point',
  todayHigh: 'sensor.todays_high_temp',
  tonightLow: 'sensor.tonights_low_temp',
  tomorrowHigh: 'sensor.tomorrows_high_temp',
  wanStatus: 'binary_sensor.unifi_dream_machine_wan_status',
  sun: 'sun.sun',
} as const

export const appliances = {
  dryer: { name: 'Tørketrommel', control: 'switch.torketrommel_shelly', energy: 'sensor.bad_torketrommel_energy' },
  washer: { name: 'Vaskemaskin', state: 'select.vaskemaskin' },
  irrigation: { name: 'Vanning', control: 'switch.vanning_shelly' },
  waterHeater: { name: 'Varmtvannsbereder', control: 'switch.heavy_duty_switch', temp: 'sensor.heavy_duty_switch_air_temperature' },
  vacuum2: {
    name: 'Roborock QR 798',
    cleanButton: 'button.kjokkenet_roborock_qr_798_fullstendig_rengjoring',
    mode: 'select.roborock_de_260443705_s5_mode_p_2_2',
  },
} as const

export const climateControls = {
  globalMode: 'input_select.climate_global_mode',
  circuitLoad: 'sensor.varmepumpe_circuit_total_load',
  heatpump: {
    nanoe: 'switch.varmepumpe_nanoe',
    swingH: 'select.varmepumpe_horizontal_swing_mode',
    swingV: 'select.varmepumpe_vertical_swing_mode',
  },
} as const

export const lightingControls = {
  scene: 'input_select.lighting_scene',
  mode: 'input_select.lighting_mode',
} as const

/** House-mode scripts (the install has 0 native scenes — these are the scenes). */
export const houseModes: Array<{ id: string; label: string; icon: string }> = [
  { id: 'script.welcome_home', label: 'Velkommen hjem', icon: 'house' },
  { id: 'script.we_are_leaving', label: 'Vi drar', icon: 'door-open' },
  { id: 'script.movie_mode', label: 'Filmkveld', icon: 'film' },
  { id: 'script.dinner_is_ready', label: 'Middag klar', icon: 'cooking-pot' },
  { id: 'script.family_is_home_day', label: 'Hjemme (dag)', icon: 'sun' },
  { id: 'script.family_is_home_night', label: 'Hjemme (kveld)', icon: 'moon' },
  { id: 'script.family_is_away', label: 'Alle borte', icon: 'shield' },
  { id: 'script.home_away', label: 'Borte-modus', icon: 'lock' },
  { id: 'script.morning_briefing', label: 'Morgenbrief', icon: 'sun' },
  { id: 'script.nightly_briefing', label: 'Kveldsbrief', icon: 'moon' },
]

export const systemHealth = {
  udmCpu: 'sensor.dream_machine_pro_cpu_utilization',
  udmMem: 'sensor.dream_machine_pro_memory_utilization',
  truenasRx: 'sensor.truenas_server_rx',
  truenasTx: 'sensor.truenas_server_tx',
  haCpu: 'sensor.ha_core_cpu_percent_2',
  haMem: 'sensor.ha_core_memory_percent_2',
  loadAvg: 'sensor.system_load_average',
  memFree: 'sensor.system_memory_free',
  memTotal: 'sensor.system_memory_total',
} as const

export const carExtras = {
  heatedSeatLeft: 'select.tessie_heated_seat_left',
  heatedSeatRight: 'select.tessie_heated_seat_right',
  heatedSteering: 'switch.tessie_heated_steering',
  valetMode: 'switch.tessie_valet_mode',
  flashLights: 'button.tessie_flash_lights',
  horn: 'button.tessie_horn',
  remoteStart: 'button.tessie_remote_start',
} as const

export const aiLayer = {
  decisionLog: '/local/ai_decision_log.txt',
  viewMode: 'input_select.ai_dashboard_view_mode',
  focus: 'input_select.ai_focus_priority',
  model: 'input_select.mcp_primary_ai_model',
  energyStrategy: 'input_select.energy_optimization_strategy',
  demandResponse: 'input_select.energy_demand_response_strategy',
} as const

export const system = {
  sonarr: { queue: 'sensor.sonarr_queue', missing: 'sensor.sonarr_missing', series: 'sensor.sonarr_series_count' },
  radarr: { queue: 'sensor.radarr_queue', missing: 'sensor.radarr_missing', movies: 'sensor.radarr_movie_count' },
  sabnzbd: { queue: 'sensor.sabnzbd_queue', speed: 'sensor.sabnzbd_speed', remaining: 'sensor.sabnzbd_remaining' },
  pihole: {
    queries: 'sensor.pi_hole_queries_today',
    blocked: 'sensor.pi_hole_blocked_today',
    percent: 'sensor.pi_hole_blocked_percent',
  },
  speedtest: { ping: 'sensor.speedtest_ping', down: 'sensor.speedtest_download', up: 'sensor.speedtest_upload' },
  watchman: {
    missingEntities: 'sensor.watchman_missing_entities',
    missingActions: 'sensor.watchman_missing_actions',
    status: 'sensor.watchman_status',
    lastUpdate: 'sensor.watchman_last_update',
  },
  uptimeKuma: 'sensor.uptime_kuma_status',
  entities: 'sensor.system_stats_unified',
  batteries: 'sensor.battery_unified',
  udmUptime: 'sensor.dream_machine_pro_uptime',
} as const
